<template>
  <form @submit.prevent="submit" class="form-group">
    <label>Prénom</label>
    <input v-model="localUser.firstName" type="text" placeholder="Entrez votre prénom"/>
    <label>Nom</label>
    <input v-model="localUser.lastName" type="text" placeholder="Entrez votre nom"/>
    <label>Email</label>
    <input v-model="localUser.email" type="email" placeholder="Entrez votre e-mail"/>
    <div class="group-rows">
      <div class="group-columns flex-1">
        <label>Genre</label>
        <select v-model="localUser.gender">
          <option value="male">Homme</option>
          <option value="female">Femme</option>
          <option value="other">Autre</option>
        </select>
      </div>
      <div class="group-columns flex-3 date-group">
        <label>Date de naissance</label>
        <input v-model="localUser.birthDate" type="date" />
      </div>
    </div>
    <div class="group-rows">
      <div class="group-columns flex-auto">
        <label>Pays</label>
        <select v-model="localUser.country" v-if="countries.length > 0" disabled class="disabled">
          <option
            v-for="country in countries"
            :key="country.id"
            :value="country.id"
          >
            {{ country.name }}
          </option>
        </select>
      </div>
      <div class="group-columns flex-auto">
        <label>Téléphone</label>
        <input v-model="localUser.phone" type="text" class="disabled" disabled />
      </div>
    </div>
    <div class="group-rows">
      <div class="group-columns flex-auto">
        <label>Moyen de paiement préféré</label>
        <select v-model="localUser.favoritePaymentMethod" v-if="paymentMethods.length > 0">
          <option
            v-for="favoritePaymentMethod in paymentMethods"
            :key="favoritePaymentMethod.value"
            :value="favoritePaymentMethod.value"
          >
            {{ favoritePaymentMethod.label }}
          </option>
        </select>
        <input
          v-else
          type="text"
          value="Aucun moyen disponible pour ce pays"
          class="disabled"
          disabled
        />
      </div>
      <div class="group-columns flex-auto">
        <label>Ville de départ par défaut</label>
          <select v-model="localUser.favoriteCity" v-if="cities.length > 0">
            <option
              v-for="city in cities"
              :key="city.id"
              :value="city.id"
            >
              {{ city.name }}
            </option>
          </select>
      </div>

    </div>
    <PasswordField ref="passwordFieldRef" />
    <div class="btn-group">
      <button class="btn btn-primary" type="submit" :disabled="loading">
        {{ loading ? 'Mise à jour...' : 'Mettre à jour' }}
      </button>
    </div>
  </form>
</template>

<script setup>
import { reactive, watch, ref, onMounted, computed } from 'vue'
import { useCountriesStore } from '@/stores/countriesStore'
import { useCitiesStore } from '@/stores/citiesStore'
import { useLocaleStore } from '@/stores/localeStore'
import { storeToRefs } from 'pinia'
import PasswordField from '../components/PasswordField.vue'
import { getPaymentMethodsForCountry } from '@/enums/paymentMethod'

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update-user'])

const localUser = reactive({
  ...props.user,
  country: props.user.country?.id || null,
  favoriteCity: props.user.favoriteCity?.id || null
})

const passwordFieldRef = ref(null)

const countriesStore = useCountriesStore()
const { countries } = storeToRefs(countriesStore)

const localeStore = useLocaleStore()

const citiesStore = useCitiesStore()
const { cities } = storeToRefs(citiesStore)

const canSeeAllPaymentMethods = computed(() =>
  Array.isArray(localUser.roles) &&
  (localUser.roles.includes('ROLE_ADMIN') || localUser.roles.includes('ROLE_SELLER'))
)

const paymentMethods = computed(() => {
  // Priorité : pays sélectionné dans le formulaire → pays du site (localeStore) → rien
  const profileCountryIso = countries.value.find(c => c.id === Number(localUser.country))?.iso3 ?? null
  const effectiveIso = profileCountryIso ?? localeStore.countryIso
  return getPaymentMethodsForCountry({
    countryIso: effectiveIso,
    includeAll: canSeeAllPaymentMethods.value,
  })
})

watch(
  () => props.user,
  (newVal) => {
    Object.assign(localUser, {
      ...newVal,
    country: newVal.country?.id || null,
    favoriteCity: newVal.favoriteCity?.id || null
    })
  }
)

onMounted(async () => {
  await Promise.all([
    countriesStore.fetchCountries(),
    citiesStore.fetchCities(),
  ])
})

watch(paymentMethods, (methods) => {
  if (methods.some(method => method.value === localUser.favoritePaymentMethod)) return
  localUser.favoritePaymentMethod = methods[0]?.value ?? ''
}, { immediate: true })

const submit = () => {
  const passwordData = passwordFieldRef.value.getValues()

  if (passwordData.newPassword && !passwordData.isValid) return

  // Champs à ne jamais envoyer dans le payload update
  const excludedKeys = ['tickets', 'roles', 'avatar', 'createdAt', 'updatedAt']

  const payload = Object.fromEntries(
    Object.entries(localUser).filter(([key]) => !excludedKeys.includes(key))
  )

  if (payload.country !== undefined) {
    payload.countryId = payload.country
    delete payload.country
  }

  if (payload.favoriteCity !== undefined) {
    payload.favoriteCityId = payload.favoriteCity
    delete payload.favoriteCity
  }

  if (passwordData.newPassword) {
    payload.newPassword = passwordData.newPassword
  }

  emit('update-user', payload)
}
</script>

<style scoped>
.form-group {
  flex: 3;
  width: 100%;
}
.group-rows {
  padding-top: 0.16rem;
}
</style>
